import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField
} from '@mui/material';
import { Close, Download, OpenInNew, Save } from '@mui/icons-material';
import ReportInteractivePreview from './ReportInteractivePreview';
import { buildPreviewHtmlFromReportData } from './reportPreviewUtils';
import { buildSectionConfigsFromPages } from './columnConfigUtils';
import { openHtmlInNewTab } from '../../utils/reportCenterApi';

/**
 * Full-screen report preview — interactive tables when reportData is provided,
 * otherwise static HTML / URL iframe fallback.
 */
const ReportPreviewDialog = ({
  open,
  title,
  reportData,
  savedPages,
  html,
  url,
  onClose,
  onOpenInNewTab,
  onDownload,
  downloadLabel = 'Download PDF',
  downloading = false,
  onDownloadCsv,
  downloadCsvLabel = 'Download CSV',
  downloadingCsv = false,
  onSaveLayout,
  saveLayoutLabel = 'Save layout to report',
  savingLayout = false,
  allowManualEdits = false,
  sectionOverrides = {},
  onSectionOverridesChange
}) => {
  const [iframeHeight, setIframeHeight] = useState(800);
  const [sectionConfigs, setSectionConfigs] = useState({});
  const [previewHeading, setPreviewHeading] = useState('');
  const [showHeading, setShowHeading] = useState(true);

  const initialSectionConfigs = useMemo(() => {
    if (!reportData?.sections?.length || !savedPages?.length) return null;
    return buildSectionConfigsFromPages(savedPages, reportData);
  }, [reportData, savedPages]);

  useEffect(() => {
    if (open) {
      setIframeHeight(800);
      setSectionConfigs(initialSectionConfigs || {});
      setPreviewHeading(reportData?.reportName || '');
      setShowHeading(true);
    }
  }, [open, html, url, reportData, initialSectionConfigs]);

  const handleSectionConfigsChange = useCallback((configs) => {
    setSectionConfigs(configs);
  }, []);

  const handleSectionOverridesChange = useCallback((next) => {
    onSectionOverridesChange?.(next || {});
  }, [onSectionOverridesChange]);

  const handleIframeLoad = useCallback((event) => {
    const iframe = event.currentTarget;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;
      const body = doc.body;
      const htmlEl = doc.documentElement;
      const height = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        htmlEl?.scrollHeight || 0,
        htmlEl?.offsetHeight || 0
      );
      if (height > 0) {
        setIframeHeight(height + 24);
      }
    } catch {
      setIframeHeight(800);
    }
  }, []);

  const handleOpenInNewTab = () => {
    if (onOpenInNewTab) {
      onOpenInNewTab({ reportData, sectionConfigs, sectionOverrides });
      return;
    }
    try {
      if (reportData) {
        openHtmlInNewTab(buildPreviewHtmlFromReportData(reportData, sectionConfigs, {
          heading: previewHeading,
          showHeading,
          sectionOverrides: allowManualEdits ? sectionOverrides : undefined
        }));
        return;
      }
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (html) {
        openHtmlInNewTab(html);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const hasInteractive = Boolean(reportData?.sections?.length);
  const hasStatic = Boolean(url || html);
  const hasContent = hasInteractive || hasStatic;

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5
        }}
      >
        <Box component="span" sx={{ pr: 2 }}>
          {title}
        </Box>
        <IconButton onClick={onClose} aria-label="close preview" edge="end">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: 0,
          overflow: 'auto',
          bgcolor: '#e8e8e8'
        }}
      >
        {reportData && (
          <Box sx={{ px: 2, pt: 2, pb: 1, bgcolor: '#f7f7f7', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                label="Preview heading"
                size="small"
                value={previewHeading}
                onChange={(e) => setPreviewHeading(e.target.value)}
                sx={{ minWidth: 280, flex: '1 1 280px' }}
                disabled={!showHeading}
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={showHeading}
                    onChange={(e) => setShowHeading(e.target.checked)}
                  />
                )}
                label="Show heading"
              />
            </Box>
          </Box>
        )}
        {hasInteractive ? (
          <Box sx={{ p: { xs: 1, sm: 2 }, minHeight: '100%' }}>
            <ReportInteractivePreview
              reportData={reportData}
              onSectionConfigsChange={handleSectionConfigsChange}
              heading={previewHeading}
              showHeading={showHeading}
              initialSectionConfigs={initialSectionConfigs}
              allowManualEdits={allowManualEdits}
              sectionOverrides={sectionOverrides}
              onSectionOverridesChange={handleSectionOverridesChange}
            />
          </Box>
        ) : hasStatic ? (
          <Box sx={{ p: { xs: 1, sm: 2 }, minHeight: '100%' }}>
            <Box
              component="iframe"
              title={title}
              src={url || undefined}
              srcDoc={url ? undefined : html}
              onLoad={handleIframeLoad}
              sx={{
                display: 'block',
                width: '100%',
                minWidth: { xs: '100%', md: '297mm' },
                maxWidth: '100%',
                height: iframeHeight,
                border: 0,
                bgcolor: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                mx: 'auto'
              }}
            />
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button
          startIcon={<OpenInNew />}
          onClick={handleOpenInNewTab}
          disabled={!hasContent}
        >
          Open in new tab
        </Button>
        {onSaveLayout && (
          <Button
            startIcon={savingLayout ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={() => onSaveLayout(sectionConfigs)}
            disabled={savingLayout || !hasInteractive}
          >
            {saveLayoutLabel}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
        {onDownloadCsv && (
          <Button
            variant="outlined"
            startIcon={downloadingCsv ? <CircularProgress size={16} color="inherit" /> : <Download />}
            onClick={() => {
              if (allowManualEdits) onDownloadCsv({ sectionOverrides });
              else onDownloadCsv();
            }}
            disabled={downloadingCsv || downloading}
          >
            {downloadCsvLabel}
          </Button>
        )}
        {onDownload && (
          <Button
            variant="contained"
            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
            onClick={() => {
              if (allowManualEdits) onDownload({ sectionOverrides });
              else onDownload();
            }}
            disabled={downloading || downloadingCsv}
          >
            {downloadLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportPreviewDialog;
